export interface ECSearchProgress {
  step?: string;
  message: string;
  plant_name?: string;
  index?: number;
  total?: number;
  count?: number;
  resultCount?: number;
  fromCache?: boolean;
  error?: string;
  timestamp?: string;
}

export interface ECSearchResult {
  success: boolean;
  results: any[];
  metadata: {
    timestamp: string;
    totalResults: number;
    fromCache: boolean;
    searchUrl?: string;
    error?: string;
  };
}

export interface ECMultiSearchResult {
  success: boolean;
  results: ECSearchResult[];
  metadata: {
    timestamp: string;
    totalSearches: number;
    completedSearches: number;
    totalTimeMs: number;
    averageTimePerSearchMs: number;
  };
}

export interface SearchRequest {
  searches?: Array<{ 
    plantName: string;
    state?: string;
    size?: string;
    region?: string;
  }>;
  plant_name?: string;
  useCache?: boolean;
  maxCacheAge?: number;
  limit?: number;
}

export const useECSearchStream = () => {
  const isSearching = ref(false);
  const progress = ref<ECSearchProgress[]>([]);
  const finalResult = ref<ECSearchResult | ECMultiSearchResult | null>(null);
  const error = ref<string | null>(null);

  let abortController: AbortController | null = null;

  const startSearch = async (searchRequest: SearchRequest) => {
    // Reset state
    isSearching.value = true;
    progress.value = [];
    finalResult.value = null;
    error.value = null;

    // Create abort controller for cancellation
    abortController = new AbortController();

    try {
      const response = await fetch('/api/ec/search', {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'x-stream': 'true'
        },
        body: JSON.stringify(searchRequest),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          let currentEvent = '';
          let accumulatedData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim();
            } else if (line.startsWith('data: ')) {
              const dataLine = line.substring(6);
              accumulatedData += dataLine;
            } else if (line.trim() === '') {
              // Empty line indicates end of event - try to parse accumulated data
              if (accumulatedData.trim()) {
                try {
                  const data = JSON.parse(accumulatedData.trim());
                  handleSSEEvent({ ...data, eventType: currentEvent });
                } catch (e) {
                  console.warn('Failed to parse SSE data:', accumulatedData.substring(0, 100) + '...', e);
                }
              }
              
              // Reset for next event
              currentEvent = '';
              accumulatedData = '';
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Search aborted by user');
      } else {
        console.error('Search error:', err);
        error.value = err instanceof Error ? err.message : 'Unknown error occurred';
      }
    } finally {
      isSearching.value = false;
      abortController = null;
    }
  };

  const handleSSEEvent = (data: any) => {
    console.log('[useECSearchStream] Received SSE event:', data.eventType, data);
    
    // Handle different types of events based on eventType
    switch (data.eventType) {
      case 'final_result':
        console.log('[useECSearchStream] Processing final result:', data);
        finalResult.value = data;
        addProgress({
          message: 'Search completed successfully',
          timestamp: new Date().toISOString()
        });
        break;
        
      case 'complete':
        console.log('[useECSearchStream] Search operation completed');
        addProgress({
          message: data.message || 'Search operation completed',
          timestamp: new Date().toISOString()
        });
        break;
        
      case 'error':
        console.log('[useECSearchStream] Error event:', data);
        error.value = data.error || data.message;
        addProgress({
          message: `Error: ${data.error || data.message}`,
          error: data.error || data.message,
          timestamp: new Date().toISOString()
        });
        break;
        
      case 'progress':
      case 'search_queued':
      case 'search_completed':
      case 'multi_search_start':
      case 'multi_search_complete':
      default:
        // Progress and general events
        console.log('[useECSearchStream] Processing progress event:', data.eventType, data);
        addProgress({
          step: data.step,
          message: data.message,
          plant_name: data.plant_name,
          index: data.index,
          total: data.total,
          count: data.count,
          resultCount: data.resultCount,
          fromCache: data.fromCache,
          timestamp: new Date().toISOString()
        });
        break;
    }
    
    // Legacy handling for events without eventType (fallback)
    if (!data.eventType) {
      if (data.success !== undefined) {
        finalResult.value = data;
        addProgress({
          message: 'Search completed successfully',
          timestamp: new Date().toISOString()
        });
      } else if (data.error) {
        error.value = data.error;
        addProgress({
          message: `Error: ${data.error}`,
          error: data.error,
          timestamp: new Date().toISOString()
        });
      }
    }
  };

  const addProgress = (progressItem: ECSearchProgress) => {
    progress.value.push(progressItem);
  };

  const stopSearch = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  const clearProgress = () => {
    progress.value = [];
    finalResult.value = null;
    error.value = null;
  };

  // Computed properties for easy access
  const latestProgress = computed(() => 
    progress.value[progress.value.length - 1] || null
  );

  const searchStats = computed(() => {
    if (!finalResult.value || !('metadata' in finalResult.value)) {
      return null;
    }

    const metadata = finalResult.value.metadata as ECMultiSearchResult['metadata'];
    return {
      totalSearches: metadata.totalSearches,
      completedSearches: metadata.completedSearches,
      totalTimeMs: metadata.totalTimeMs,
      averageTimePerSearchMs: metadata.averageTimePerSearchMs,
      successfulSearches: finalResult.value.results?.filter((r: any) => r.success).length || 0,
      failedSearches: finalResult.value.results?.filter((r: any) => !r.success).length || 0
    };
  });

  const progressByPlant = computed(() => {
    const byPlant: Record<string, ECSearchProgress[]> = {};
    
    progress.value.forEach(p => {
      if (p.plant_name) {
        if (!byPlant[p.plant_name]) {
          byPlant[p.plant_name] = [];
        }
        byPlant[p.plant_name].push(p);
      }
    });
    
    return byPlant;
  });

  return {
    // State
    isSearching: readonly(isSearching),
    progress: readonly(progress),
    finalResult: readonly(finalResult),
    error: readonly(error),
    
    // Computed
    latestProgress,
    searchStats,
    progressByPlant,
    
    // Methods
    startSearch,
    stopSearch,
    clearProgress
  };
};